<template>
    <van-config-provider theme="dark" :class="{ 'pc-container': !isMobile }">
        <!--        <router-view />-->
        <router-view v-slot="{ Component }">
            <component :is="Component" :key="route.fullPath" />
        </router-view>

        <hongbao v-if="route.name !== 'home' && route.name !== 'liveMobile'" />
    </van-config-provider>

    <!-- 使用van的popup组件 -->
    <van-popup close-on-click-overlay class="popup-download-ios" v-model:show="isShowPopup">
        <img @click="isShowPopup = false" src="/assets/pwa/download_ios_en.png" alt="" />
    </van-popup>

    <!-- 下载App的弹窗 -->
    <van-popup close-on-click-overlay class="popup-apk-alert" v-model:show="isShowApkAlert">
        <dialog-apkalert @cancel="handleCancel" @install="handleInstall" />
    </van-popup>

    <!-- <RbLayer v-model="isShowInstall" :pwa-config="pwaConfig" @cancel="handleRbCancel" @pwa-installed="handlePwaInstalled" /> -->

    <!-- H5环境下打开的pwa更新提示弹窗 -->
    <van-popup style="background: none" :close-on-click-overlay="isClosePwaUpdate" class="popup-pwa-update" v-model:show="pwaUpdateVisible">
        <pwa-update @click="handlePwaUpdateClick" />
    </van-popup>

    <!-- GoogleToApk弹窗 -->
    <van-popup style="background: none" :close-on-click-overlay="isCloseGoogleToApk" class="popup-pwa-update" v-model:show="googleToApkVisible">
        <google-to-apk @click="handleGoogleToApkClick" />
    </van-popup>

    <!-- PWA安装状态弹窗 -->
    <pwa-install-modal @play="handlePwaPlay" />

    <loading-box v-if="isShowLoading" />

    <!-- 全局刷新按钮 -->
    <XbRefreshButton />

    <!-- 主题切换组件 -->
    <!-- <ThemeSwitcher /> -->
    
    <!-- 星空主题背景 -->
    <StarBackground />
</template>

<script setup lang="ts">
import useApp from 'core/hooks/useApp';
import { SystemDataType } from 'types/appConfig';
import { computed, onUnmounted, getCurrentInstance, onMounted, ref, watch, nextTick } from 'vue';
import { ConfigProvider } from 'vant';
import HiCache from 'utils/cache';
import { PWAManifestConfig, usePWA } from 'core/hooks/usePWA';
import { TemplateDataType } from './views/home/components/web-temp-v3.vue';
import usePwaClient from 'core/hooks/usePwaClient';
import { initFirebasePWA } from 'app/hook/firebase/index';
import { useTransactionListener } from 'app/hook/useTransactionListener';
import user from 'service/user';
import PwaInstallModal from '@/components/pwa-install-modal/pwa-install-modal.vue';
import HiStance from 'utils/HiStance';
import GoogleToApk from '@/components/pwa-update/google-to-apk.vue';
import XbRefreshButton from '@/components/common/xb-refresh-button.vue';
import StarBackground from '@/components/star-background/star-background.vue';

const { updateManifest } = usePWA();
const { t, appStore, userStore, route, router, browserDetector, mitt, getAndroidVersion, toast, showLoadingToast } = useApp();
const {
    webApp,
    getUrlParams,
    getCookie: getPwaCookie,
    handleRouteAndWebviewParams,
    isShowPwaUpdate,
    setupPwaUpdateWatch,
    getIsClosePwaUpdate,
    isPwaInstalled,
    isRecoveredFromUninstall,
    getClientType,

    isShowGoogleToApk,
    setupGoogleToApkWatch,
    getIsCloseGoogleToApk,
    isGoogleMode,
} = usePwaClient();

const systemData = computed(() => appStore.getSystemData);
const isLogin = computed(() => userStore.IsLogin);
const userInfo = computed(() => userStore.userInfo);

const isShowPwaUpdateDialog = computed(() => {
    const andriodVersion = getAndroidVersion();
    if (andriodVersion && parseInt(andriodVersion) < 9 && parseInt(andriodVersion) !== 1) {
        return false;
    }
    return isShowPwaUpdate.value;
});

// 添加移动端检测
const isMobile = computed(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
});

// 添加一个可修改的ref变量用于控制弹窗
const pwaUpdateVisible = ref(false);
const googleToApkVisible = ref(false);

// 监听计算属性变化，更新弹窗显示状态
watch(isShowPwaUpdateDialog, newVal => {
    pwaUpdateVisible.value = newVal;
});

// 监听计算属性变化，更新弹窗显示状态
watch(isShowGoogleToApk, newVal => {
    googleToApkVisible.value = newVal;
});

// 初始化时同步一次状态
onMounted(() => {
    pwaUpdateVisible.value = isShowPwaUpdateDialog.value;
    googleToApkVisible.value = isShowGoogleToApk.value;
});

nextTick(() => {
    document.getElementById('skeleton-screen')?.remove();
});

function init() {
    modifyIco(systemData.value as SystemDataType);
    userStore.getCarouselListTop();

    if (isLogin.value) {
        userStore.handleGetUserInfo();
        handleIosPwaUrl();
    }
}

function adjustHeight() {
    if (isMobile.value) {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
    }
}

async function handleVisit() {
    try {
        const userVisit = HiCache.getCache('user_visit');

        if (userVisit) {
            return;
        }
        const { code } = route.query;
        const params: any = {};
        let str = (code as string) || HiCache.getCache('spreaderId') || '';
        let spreaderId = str.match(/\d+/g);
        params.spreaderId = Number(spreaderId?.length) > 0 ? (spreaderId?.[0] as string) : (code as string) || HiCache.getCache('spreaderId') || '';
        params.channel = HiCache.getCache('app_fb_pixel_id') || '';
        params.domain = systemData.value?.domain || '';
        // 设置客户端类型
        params.client = getClientType();
        params.deviceId = appStore.GetFingerprintId || '';
        const res = await userStore.handleUserVisit(params);
        if (res.code === 0) {
            HiCache.setCache('user_visit', true);
        }
    } catch (error) {
        console.error('用户访问网站失败', error);
    }
}

// 监听窗口大小变化
window.addEventListener('resize', adjustHeight);

// 组件卸载时移除事件监听
onUnmounted(() => {
    window.removeEventListener('resize', adjustHeight);
    // 停止交易监听
    stopListening();
});

// 设置icon
function modifyIco(data: SystemDataType) {
    // 查找或创建一个特定ID的favicon链接
    let link = document.getElementById('app-site-favicon') as HTMLLinkElement;
    if (!link) {
        link = document.createElement('link');
        link.id = 'app-site-favicon';
        link.rel = 'shortcut icon';
        link.type = 'image/x-icon';
        document.head.appendChild(link);
    }
    link.href = data?.ico;
    document.title = data?.siteName || '';
}

// 配置 Vant 全局选项
onMounted(() => {
    const app = getCurrentInstance()?.appContext.app;
    if (app) {
        // 设置弹窗默认挂载点
        app.config.globalProperties.$vant = {
            // 将弹窗挂载到 #app 内的容器中
            popupContainer: '#popup-container',
        };
    }
});

// 将代码调整为先初始化PWA配置，再初始化网站基本配置
// PWA相关初始化
// const isShowInstall = ref(false);
const pwaConfig = ref<PWAManifestConfig>({
    name: '',
    shortName: '',
    description: '',
    themeColor: '#007135',
    backgroundColor: '#ffffff',
    iconUrl: '',
    startUrl: window.pwaurl,
    screenshots: [],
});

const urlParams = getUrlParams();
const { v, fbclid } = urlParams as any;

const appKeyRef = computed(() => {
    return systemData.value?.appKey;
});
const tempDetail = ref<TemplateDataType>();

const config = computed(() => appStore.getSystemData);

// 先获取模板配置，设置PWA
async function initTemplate(id: any) {
    try {
        const res = await appStore.handleGetPromotionTemplate({ id });
        if (res?.code === 0) {
            tempDetail.value = res?.data;
            appStore.setPwaTempConfig(tempDetail.value);
            // isGoogle.value = tempDetail.value?.templateType === 1;

            // 立即更新 PWA 配置
            pwaConfig.value = {
                iconUrl: getSrc(tempDetail.value?.appIcon || ''),
                name: tempDetail.value?.appName || '',
                shortName: tempDetail.value?.appName || '',
                description: tempDetail.value?.appInfo || '',
                themeColor: '#007135',
                backgroundColor: '#ffffff',
                startUrl: window.pwaurl,
                screenshots: tempDetail.value?.storeIconList?.map((item: any) => {
                    return {
                        src: getSrc(item.url || ''),
                        sizes: '551x979',
                        type: 'image/jpeg',
                    };
                }),
            };

            localStorage.setItem('pwaiconUrl', pwaConfig.value?.iconUrl || '');

            appStore.setPwaConfig(pwaConfig.value);

            document.title = tempDetail.value?.appName || '';
        }
    } catch (e) {}
}

// 添加交易监听
const { startListening, stopListening } = useTransactionListener();

// 初始化执行顺序：
// 1. 先初始化模板 (initTemplate)
// 2. 然后执行常规初始化 (init) - 确保modifyIco在最后运行
async function initAll() {
    // 先初始化PWA配置
    await initTemplate(v || 9);

    // 然后执行常规初始化，包括设置favicon
    init();
    adjustHeight();
    handleRouteAndWebviewParams();
    handleVisit();

    // 启动交易监听
    if (isLogin.value) {
        const result = startListening();
        console.log('交易监听初始化:', result.message);
    }
}

initAll();

function getSrc(url: string) {
    const getTimes = new Date().getTime();
    if (config.value?.cdn && url) {
        return `${config.value?.cdn}${url}?time=${getTimes}`;
    } else {
        return '';
    }
}

// ======================================= pwa相关逻辑 ======================================= //
const userinfo = computed(() => userStore.GetUserInfo);

// 设置PWA更新提示监听
setupPwaUpdateWatch(userinfo);

// 设置GoogleToApk监听
setupGoogleToApkWatch(userinfo);

// 点击遮罩是否关闭pwa更新弹窗
const isClosePwaUpdate = computed(() => getIsClosePwaUpdate(userinfo));

// 点击遮罩是否关闭GoogleToApk弹窗
const isCloseGoogleToApk = computed(() => getIsCloseGoogleToApk(userinfo));

function handlePwaUpdateClick() {
    // 直接关闭弹窗
    pwaUpdateVisible.value = false;
    downloadApp();
}

function handleGoogleToApkClick() {
    console.log('跳转浏览器 下载apk');

    const apkDownloadUrl = appStore.GetGlobalApkDownloadUrl;
    openInDefaultBrowser(apkDownloadUrl || systemData.value?.androidAppUrl);
}

const openInDefaultBrowser = url => {
    const cleanUrl = url.replace(/^https?:\/\//, '');
    // 使用Chrome包名，如果没有Chrome会使用fallback URL
    const intentUrl = `intent://${cleanUrl}#Intent;scheme=https;package=com.android.chrome;S.browser_fa
  llback_url=${encodeURIComponent(url)};end`;

    window.location.href = intentUrl;
};

const isShowPopup = ref(false);
const isShowApkAlert = ref(false);

const isShowLoading = ref(false);

async function downloadApp() {
    await handlePwaToken();

    try {
        // 检测设备类型
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = browserDetector.isAndroidDevice();

        if (isIOS) {
            // iOS设备显示iOS下载弹窗
            isShowPopup.value = true;
            return;
        }

        if (!isAndroid) {
            // 非安卓设备显示通用下载弹窗
            isShowApkAlert.value = true;
            return;
        }

        // 安卓设备执行Google下载逻辑
        await gotoGoogleDownload();
    } catch (e) {
        // 出错时显示APK下载弹窗作为备用方案
        isShowApkAlert.value = true;
    }
}

// 处理pwaToken
async function handlePwaToken(isLoading = true) {
    if (isLogin.value) {
        const loading = isLoading
            ? showLoadingToast({
                  icon: getLoadingImg(),
                  overlay: true,
                  className: 'loading-toast',
                  forbidClick: true,
                  duration: 0,
              })
            : null;

        try {
            const result = await user.pwaTokenGenerate();

            const { code, data } = result;
            if (code === 0 && data) {
                const url = new URL(window.pwaurl);
                url.searchParams.set('token', data);
                const newUrl = url.toString();

                pwaConfig.value = {
                    ...pwaConfig.value,
                    startUrl: newUrl,
                };
                window.pwaurl = newUrl;
                appStore.setPwaConfig(pwaConfig.value);
            }
        } catch (e) {
            console.log(e, '=======e');
        } finally {
            loading?.close();
        }
    }
}

// 配置常量
const CONFIG = {
    // API 基础路径配置
    API_BASE: window.location.protocol + '//' + window.location.host,

    // Chrome包名
    CHROME_PACKAGE: 'com.android.chrome',

    // 调试开关
    DEBUG: false,
};

// 日志工具
const logger = {
    log(msg, data = null) {
        if (CONFIG.DEBUG) {
            console.log(`[GoogleDownload] ${msg}`, data || '');
        }
    },

    error(msg, err = null) {
        if (CONFIG.DEBUG) {
            console.error(`[GoogleDownload Error] ${msg}`, err || '');
        }
    },
};

// URL处理工具
const urlUtils = {
    // 构建Intent URL
    buildIntentUrl(url) {
        const strippedUrl = url.replace(location.protocol + '//', '');
        return `intent://${strippedUrl}#Intent;scheme=https;package=${CONFIG.CHROME_PACKAGE};end`;
    },

    // 检测Intent URL跳转是否超时
    checkIntentTimeout(timeoutMs = 3000) {
        return new Promise(resolve => {
            const startTime = Date.now();

            // 记录当前页面可见状态
            const initialVisibility = document.visibilityState;

            // 设置检测间隔
            const checkInterval = 100; // 每100ms检测一次

            // 检测函数
            const checkVisibility = () => {
                // 如果页面变为不可见，说明跳转可能成功
                if (document.visibilityState !== initialVisibility && document.visibilityState === 'hidden') {
                    clearInterval(intervalId);
                    resolve(true); // 跳转成功
                }

                // 如果超时，说明跳转可能失败
                if (Date.now() - startTime > timeoutMs) {
                    clearInterval(intervalId);
                    resolve(false); // 跳转失败
                }
            };

            // 设置定时检测
            const intervalId = setInterval(checkVisibility, checkInterval);
        });
    },
};

// 跳转到 Google Play 下载
async function gotoGoogleDownload() {
    try {
        logger.log('开始处理下载跳转');

        // 获取当前URL
        let redirectUrl = window.location.href;

        // 判断浏览器环境
        const isSpecialBrowser =
            browserDetector.isFacebookApp() ||
            browserDetector.isInstagramApp() ||
            browserDetector.isKwaiApp() ||
            browserDetector.isTikTokBrowser() ||
            browserDetector.isSamsungBrowser() ||
            browserDetector.isMiuiBrowser() ||
            browserDetector.isVivoBrowser() ||
            browserDetector.isOppoBrowser() ||
            browserDetector.isUCBrowser();

        if (isSpecialBrowser) {
            // 尝试唤醒Chrome浏览器
            try {
                // 检测Chrome浏览器是否已安装
                const isChromeInstalled = await browserDetector.isChromeInstalled();

                if (!isChromeInstalled) {
                    logger.log('Chrome浏览器未安装，显示APK下载弹窗');
                    isShowApkAlert.value = true;
                    return;
                }

                // 社交App内使用Intent scheme跳转
                const intentUrl = urlUtils.buildIntentUrl(redirectUrl);
                logger.log('使用Intent跳转', intentUrl);

                // 记录尝试跳转的时间
                const intentStartTime = Date.now();
                localStorage.setItem('intent_start_time', intentStartTime.toString());

                // 尝试打开Chrome
                window.open(intentUrl, '_blank');

                // 检测Intent URL跳转是否超时
                const isSuccess = await urlUtils.checkIntentTimeout(3000);

                if (!isSuccess) {
                    logger.log('Intent跳转超时，显示APK下载弹窗');
                    // 显示APK下载弹窗作为备用方案
                    isShowApkAlert.value = true;
                }
            } catch (intentError) {
                // Intent URL打开失败，显示APK下载弹窗
                logger.error('Intent跳转失败', intentError);
                isShowApkAlert.value = true;
            }
        } else {
            if (browserDetector.isGoogleBrowser()) {
                console.log(isPwaInstalled.value, '-----isPwaInstalled.value');
                // 使用新的PWA安装检测逻辑
                if (isPwaInstalled.value) {
                    window.open(window.pwaurl, '_blank');
                    return;
                }

                // 如果检测到PWA未安装，但deferredPrompt为null
                // 这种情况可能是浏览器不支持PWA安装，或已安装但检测失败
                console.log(window.deferredPrompt, '-----window.deferredPrompt');
                if (!window.deferredPrompt) {
                    // 仍然尝试打开PWA，因为有些浏览器可能安装后立即清除了deferredPrompt
                    window.open(window.pwaurl, '_blank');
                    return;
                }

                // 如果能走到这里，说明未安装PWA，但支持安装（deferredPrompt存在）
                setTimeout(async () => {
                    // isShowInstall.value = true;
                    await ultimateInstall();
                }, 300);
            } else {
                isShowApkAlert.value = true;
            }
        }
    } catch (err) {
        logger.error('跳转失败', err);
        // 任何错误情况下，都显示APK下载弹窗作为备用方案
        isShowApkAlert.value = true;
    }
}

function handleCancel() {
    isShowApkAlert.value = false;
}

function handleInstall() {
    isShowApkAlert.value = false;
    isShowLoading.value = true;

    const apkDownloadUrl = appStore.GetGlobalApkDownloadUrl;

    try {
        // 获取下载地址
        const downloadUrl = apkDownloadUrl ? apkDownloadUrl : systemData.value?.androidAppUrl;

        // 检查URL是否存在
        if (!downloadUrl) {
            toast({
                duration: 2000,
                message: t('下载地址不存在，请稍后重试'),
                type: 'fail',
            });
            isShowLoading.value = false;
            return;
        }

        // 记录下载事件

        // 创建隐藏的a标签来触发下载
        const downloadLink = document.createElement('a');
        downloadLink.href = downloadUrl;
        downloadLink.target = '_blank';
        downloadLink.rel = 'noopener noreferrer';
        downloadLink.style.display = 'none';

        // 将链接添加到DOM并点击
        document.body.appendChild(downloadLink);
        downloadLink.click();

        // 移除链接元素
        setTimeout(() => {
            document.body.removeChild(downloadLink);
            isShowLoading.value = false;
        }, 1000);

        // 提示用户
        setTimeout(() => {
            toast({
                duration: 2000,
                message: t('下载已开始，请在通知栏查看下载进度'),
                type: 'success',
            });
        }, 1500);
    } catch (error) {
        isShowLoading.value = false;
        toast({
            duration: 2000,
            message: t('下载失败，请稍后重试'),
            type: 'fail',
        });
    }
}

function handleRbCancel() {
    isShowApkAlert.value = true;
}

// 处理PWA安装成功事件
function handlePwaInstalled() {
    console.log('收到PWA安装成功通知');
}

mitt.on('downloadApp', () => {
    console.log('接收下载App');
    downloadApp();
});

const { isPwaMode, webApp: isWebApp } = usePwaClient();

// PWA 模式下初始化消息推送功能
watch(
    isPwaMode,
    newVal => {
        console.log(newVal, 'pwa模式下执行 Firebase Messaging 初始化');
        if (newVal) {
            // 只在 PWA 模式下初始化消息推送功能
            initFirebasePWA();
        }
    },
    {
        immediate: true,
        deep: true,
    }
);

// 应用启动时初始化 Firebase 核心服务（Analytics、Performance、Database）
// onMounted(async () => {
//     // 初始化 Firebase 核心服务（不管是否为 PWA 模式）
//     console.log('初始化 Firebase 核心服务');
//     await initFirebase();
    
//     // 初始化 Realtime Database 功能
//     console.log('初始化 Realtime Database');
//     useRealtimeDatabase();
// });

// 监听登录状态变化
watch([isLogin, userInfo], ([newVal, newUserInfo]) => {
    if (newVal && newUserInfo) {
        // 登录后启动交易监听
        const result = startListening();
        console.log('登录后启动交易监听:', result.message);

        // 🎯 超简单：登录成功后备份缓存到父页面
        backupCacheToParent();
    } else {
        // 登出时停止监听
        stopListening();
    }
});

// 🎯 极简缓存同步 - 让iframe缓存永远不丢失！
const CACHE_KEYS = ['token', 'refresh_token', 'userInfo', 'loginState', 'gamePreferences'];

// 备份缓存到父页面
function backupCacheToParent() {
    try {
        const cacheData = {};
        CACHE_KEYS.forEach(key => {
            const value = localStorage.getItem(key);
            if (value) cacheData[key] = value;
        });

        if (Object.keys(cacheData).length > 0) {
            // 发送给父页面备份
            window.parent.postMessage(
                {
                    type: 'CACHE_BACKUP',
                    cacheData,
                },
                '*'
            );
            console.log('✅ 缓存已备份到父页面');
        }
    } catch (error) {
        console.log('缓存备份失败:', error);
    }
}

// 从父页面恢复缓存
function restoreCacheFromParent(cacheData) {
    try {
        Object.entries(cacheData).forEach(([key, value]) => {
            localStorage.setItem(key, value as any);
        });
        console.log('✅ 缓存已从父页面恢复');

        // 🎯 立即更新Vue响应式状态
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refresh_token');
        const userInfoStr = localStorage.getItem('userInfo');

        if (token && refreshToken && userInfoStr) {
            try {
                const userInfoObj = JSON.parse(userInfoStr);
                // 使用userStore的方法正确更新状态
                userStore.saveToken({ token: HiCache.getCache(HiStance.TOKEN), refresh_token: HiCache.getCache(HiStance.REFRESH_TOKEN), salt: '' });
                userStore.updateUserInfo(userInfoObj);
                console.log('✅ Vue状态已更新');
            } catch (error) {
                console.log('解析用户信息失败:', error);
            }
        }

        // 触发自定义事件通知应用缓存已恢复
        window.dispatchEvent(new CustomEvent('cacheRestored'));
    } catch (error) {
        console.log('缓存恢复失败:', error);
    }
}

// 监听来自父页面的消息
window.addEventListener('message', event => {
    if (event.data?.type === 'CACHE_RESTORE' && event.data.cacheData) {
        restoreCacheFromParent(event.data.cacheData);
    }
});

// 定期备份缓存（每分钟一次）
setInterval(backupCacheToParent, 60000);

// 登录成功时立即备份
window.addEventListener('loginSuccess', backupCacheToParent);

onMounted(() => {
    // 页面加载时备份一次现有缓存
    setTimeout(backupCacheToParent, 2000);
});

// iOS对pwaUrl的处理
async function handleIosPwaUrl() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
        await handlePwaToken(false);
    }
}

// ================================================ pwa 安装逻辑 start ================================================ //
const actualPwaConfig = ref<PWAManifestConfig | null>(null);

// 用户是否接受安装
const isUserAcceptInstall = ref(false);

watch(
    () => pwaConfig.value,
    async newConfig => {
        if (newConfig) {
            // 深拷贝配置以避免响应式问题
            const config = JSON.parse(JSON.stringify(newConfig));

            if (!config.name || !config.iconUrl) {
                return;
            }

            // 存储实际配置
            actualPwaConfig.value = config;

            // console.log(actualPwaConfig.value, 'actualPwaConfig.value');

            // 设置全局配置，供 iOS 自动更新使用
            window.pwaConfig = config;

            // 立即更新 manifest
            try {
                await updateManifest(config);
                console.log('manifest 更新成功');

                // 检查是否可以安装
                if (window.deferredPrompt) {
                    console.log('检测到可以安装 PWA');
                }
            } catch (error) {
                console.error('更新 manifest 失败:', error);
            }
        }
    },
    { immediate: true, deep: true }
);

// 是否正在安装pwa
const isInstalling = ref(false);

// 终极安装逻辑
async function ultimateInstall() {
    if (isInstalling.value) return;

    if (isUserAcceptInstall.value) {
        window.open(window.pwaurl, '_blank');
        return;
    }

    try {
        // 检查配置是否就绪
        if (!actualPwaConfig.value || !actualPwaConfig.value.name || !actualPwaConfig.value.iconUrl) {
            console.log('等待 PWA 配置就绪...');
            return;
        }
        // 使用实际的配置更新 manifest
        const config = JSON.parse(JSON.stringify(actualPwaConfig.value)); // 深拷贝避免响应式问题
        console.log('更新 manifest 配置:', config);
        // await updatePwaUrl();

        // 等待一下确保 beforeinstallprompt 事件已经被处理
        await new Promise(resolve => setTimeout(resolve, 100));

        if (!window.deferredPrompt) {
            console.log('不支持pwa - deferredPrompt 不存在，等待事件触发...');
            // 等待 beforeinstallprompt 事件
            await new Promise<void>(resolve => {
                const handler = () => {
                    window.removeEventListener('pwa-installable', handler);
                    resolve();
                };
                window.addEventListener('pwa-installable', handler);

                // 更新 manifest 可能会触发新的 beforeinstallprompt 事件
                updateManifest(config).catch(console.error);
            });

            // 再次检查 deferredPrompt
            if (!window.deferredPrompt) {
                console.log('仍然不支持 PWA 安装');
                return;
            }
        }

        // 获取当前的 promptEvent，并立即清除全局变量
        const promptEvent = window.deferredPrompt;
        window.deferredPrompt = null;

        // 开始PWA安装流程
        console.log('开始PWA安装流程', promptEvent);
        try {
            // 触发安装提示
            console.log('调用 prompt() 之前...');
            await promptEvent.prompt();
            console.log('prompt() 调用完成');

            // 等待用户响应
            const choiceResult = await promptEvent.userChoice;
            console.log('用户选择结果:', choiceResult);

            if (choiceResult.outcome === 'accepted') {
                console.log('用户接受安装');
                localStorage.setItem('pwa_install_time', Date.now().toString());
                localStorage.setItem('sdmode', '2');

                // 触发pwa安装事件（来自App.vue全局逻辑）
                mitt.emit('PWA_START_INSTALL', {
                    source: 'app',
                });

                isUserAcceptInstall.value = true;
                // startInstallProgress();
                // isUserAcceptInstall.value = true;
            } else {
                console.log('用户拒绝安装');
                handleRbCancel();
            }
        } catch (promptError) {
            console.error('安装提示显示失败:', promptError);
        }
    } catch (error) {
        console.error('PWA installation failed:', error);
    } finally {
    }
}

// ================================================ pwa 安装逻辑 end ================================================ //

// 处理PWA Play按钮点击
const handlePwaPlay = () => {
    // 当用户点击Play按钮时，打开PWA
    console.log('用户点击PWA Play按钮');
    window.open(window.pwaurl, '_blank');
};
</script>

<style scoped>
.pc-container {
    position: relative;
    margin: 0 auto;
    height: 100%;
    overflow: hidden;
}
</style>
